import {useId, useState, useMemo} from "react";
import {
    Box, Stack, Typography, Breadcrumbs, Link as MLink,
    IconButton, Collapse, Divider
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import {Link as RouterLink, useLocation, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";

function normalizeLabelConfig(val) {
    if (val == null) return null;
    return typeof val === "string" ? { text: val } : { ...val };
}

function RouterBreadcrumbs({
                               labels,                     // { segKey: string | {text, link?, to?, ariaLabel?} }
                               paramLabels,                // { paramName: string | {text, link?, to?, ariaLabel?} }
                               hideUnlabeledParams = true,
                               linkLast = false,
                               color = "inherit",
                               sx,
                           }) {
    const { pathname } = useLocation();
    const params = useParams();
    const { t } = useTranslation();

    // Map actual param values --> normalized label objects (or null marker)
    const paramValueToLabel = useMemo(() => {
        const map = new Map();
        if (params) {
            for (const [pName, pValue] of Object.entries(params)) {
                if (!pValue) continue;
                const cfg = normalizeLabelConfig(paramLabels?.[pName]);
                // cfg === null/undefined means “known param but no label provided”
                map.set(String(pValue).toLowerCase(), cfg ?? null);
            }
        }
        return map;
    }, [params, paramLabels]);

    const crumbs = useMemo(() => {
        const parts = pathname.split("/").filter(Boolean);
        const out = [{ label: { text: t("menu.label1") }, to: "/" }];

        let acc = "";
        parts.forEach((seg, i) => {
            const key = seg.toLowerCase();
            const isLast = i === parts.length - 1;

            // 1) literal segment label (from `labels`)
            let cfg = normalizeLabelConfig(labels?.[key]);

            // 2) param value label (from `paramLabels`) if not provided above
            if (!cfg && paramValueToLabel.has(key)) {
                const mapped = paramValueToLabel.get(key); // object or null
                if (mapped === null && hideUnlabeledParams) return; // skip raw :param
                cfg = mapped; // may still be null -> fallback to raw text later
            }

            // Build href
            acc += `/${seg}`;
            const defaultTo = (!isLast || linkLast) ? acc : undefined;

            // Final label object with fallbacks
            const labelObj = {
                text: cfg?.text ?? key,
                ariaLabel: cfg?.ariaLabel,
            };

            // Resolve link control:
            // - if cfg.link === false => no link
            // - if cfg.link === true  => force link
            // - else use defaultTo
            const to = cfg?.link === false ? undefined : (cfg?.to ?? (cfg?.link === true ? acc : defaultTo));

            out.push({ label: labelObj, to });
        });

        return out;
    }, [pathname, labels, linkLast, t, paramValueToLabel, hideUnlabeledParams]);

    return (
        <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={sx}
        >
            {crumbs.map((c, idx) =>
                c.to ? (
                    <MLink
                        key={idx}
                        component={RouterLink}
                        to={c.to}
                        underline="hover"
                        color={color}
                        aria-label={c.label.ariaLabel}
                        sx={{ textDecoration: "none", "&:hover": { textDecoration: "underline !important" } }}
                    >
                        {c.label.text}
                    </MLink>
                ) : (
                    <Typography key={idx} color="text.primary" aria-label={c.label.ariaLabel}>
                        {c.label.text}
                    </Typography>
                )
            )}
        </Breadcrumbs>
    );
}

export default function PageHeader({
                                       title,
                                       subtitle,
                                       actions,
                                       divider = true,
                                       subtitleDefaultExpanded = false,
                                       onSubtitleToggle,
                                       subtitleToggleAriaLabel = "Toggle details",
                                       labels,
                                       paramLabels,
                                       hideUnlabeledParams = true,
                                       linkLast = false,
                                       color = "inherit",
                                   }) {
    const subtitleId = useId();
    const [expanded, setExpanded] = useState(!!subtitleDefaultExpanded);
    const hasSubtitle = !!subtitle;

    const handleToggle = () => {
        const next = !expanded;
        setExpanded(next);
        onSubtitleToggle?.(next);
    };

    return (
        <Box sx={{mb: 1.5}}>
            <Stack spacing={0.75}>
                <RouterBreadcrumbs
                    labels={labels}
                    paramLabels={paramLabels}
                    hideUnlabeledParams={hideUnlabeledParams}
                    linkLast={linkLast}
                    color={color}
                    sx={{ mb: 0.25 }}
                />

                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{minWidth: 0}}>
                        <Typography variant="h5" component="h1" sx={{lineHeight: 1.2, fontWeight: "bold"}}>
                            {title}
                        </Typography>

                        {hasSubtitle && (
                            <IconButton
                                size="small"
                                onClick={handleToggle}
                                aria-label={subtitleToggleAriaLabel}
                                aria-controls={subtitleId}
                                aria-expanded={expanded}
                            >
                                <ExpandMoreIcon
                                    fontSize="small"
                                    sx={{
                                        transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                                        transition: "transform 150ms ease",
                                    }}
                                />
                            </IconButton>
                        )}
                    </Stack>

                    {actions ? (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{flexShrink: 0}}>
                            {actions}
                        </Stack>
                    ) : null}
                </Stack>

                {hasSubtitle && (
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Typography id={subtitleId} variant="body2" color="text.secondary" sx={{mt: 0.25}}>
                            {subtitle}
                        </Typography>
                    </Collapse>
                )}
            </Stack>

            {divider && <Divider sx={{mt: 1.25}}/>}
        </Box>
    );
}
